import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import ConfigProvider from "../components/ui/ConfigProvider";
import { themeConfig } from "../configs/theme.config";
import { MODE_DARK, MODE_LIGHT } from "../constants/theme.constant";
import {
  initialThemeState,
  type ThemeDirection,
  type ThemeLayoutType,
  type ThemeMode,
  type ThemeNavMode,
  type ThemeState,
  themeReducer,
} from "./themeReducer";

export type ThemeContextValue = ThemeState & {
  setDirection: (direction: ThemeDirection) => void;
  setMode: (mode: ThemeMode) => void;
  setLocale: (locale: string) => void;
  setLayout: (layout: ThemeLayoutType) => void;
  setPreviousLayout: (layout: ThemeLayoutType | undefined) => void;
  setLoadingLayout: () => void;
  setSideNavCollapse: (collapsed: boolean) => void;
  setNavMode: (navMode: ThemeNavMode | "default") => void;
  setPanelExpand: (expanded: boolean) => void;
  setThemeColor: (color: string) => void;
  setThemeColorLevel: (level: number) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, dispatch] = useReducer(themeReducer, initialThemeState);

  const setDirection = useCallback(
    (direction: ThemeDirection) =>
      dispatch({ type: "setDirection", payload: direction }),
    [],
  );
  const setMode = useCallback(
    (mode: ThemeMode) => dispatch({ type: "setMode", payload: mode }),
    [],
  );
  const setLocale = useCallback(
    (locale: string) => dispatch({ type: "setLocale", payload: locale }),
    [],
  );
  const setLayout = useCallback(
    (layout: ThemeLayoutType) =>
      dispatch({ type: "setLayout", payload: layout }),
    [],
  );
  const setPreviousLayout = useCallback(
    (layout: ThemeLayoutType | undefined) =>
      dispatch({ type: "setPreviousLayout", payload: layout }),
    [],
  );
  const setLoadingLayout = useCallback(
    () => dispatch({ type: "setLoadingLayout" }),
    [],
  );
  const setSideNavCollapse = useCallback(
    (collapsed: boolean) =>
      dispatch({ type: "setSideNavCollapse", payload: collapsed }),
    [],
  );
  const setNavMode = useCallback(
    (navMode: ThemeNavMode | "default") =>
      dispatch({ type: "setNavMode", payload: navMode }),
    [],
  );
  const setPanelExpand = useCallback(
    (expanded: boolean) =>
      dispatch({ type: "setPanelExpand", payload: expanded }),
    [],
  );
  const setThemeColor = useCallback(
    (color: string) => dispatch({ type: "setThemeColor", payload: color }),
    [],
  );
  const setThemeColorLevel = useCallback(
    (level: number) => dispatch({ type: "setThemeColorLevel", payload: level }),
    [],
  );

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const root = document.documentElement;
    root.classList.remove(theme.mode === MODE_DARK ? MODE_LIGHT : MODE_DARK);
    root.classList.add(theme.mode === MODE_DARK ? MODE_DARK : MODE_LIGHT);
    root.setAttribute("dir", theme.direction);
  }, [theme.direction, theme.mode]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      ...theme,
      setDirection,
      setMode,
      setLocale,
      setLayout,
      setPreviousLayout,
      setLoadingLayout,
      setSideNavCollapse,
      setNavMode,
      setPanelExpand,
      setThemeColor,
      setThemeColorLevel,
    }),
    [
      theme,
      setDirection,
      setMode,
      setLocale,
      setLayout,
      setPreviousLayout,
      setLoadingLayout,
      setSideNavCollapse,
      setNavMode,
      setPanelExpand,
      setThemeColor,
      setThemeColorLevel,
    ],
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider value={{ ...themeConfig, ...theme }}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const theme = useContext(ThemeContext);
  if (!theme) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return theme;
}
